import React from 'react';
import { get, filter, find, cloneDeep, isNil, merge, isEmpty } from 'lodash';

// local dependencies
import { memberInfoCommunicationSettingsEmail as MemberInfoCommunicationSettingsEmailView } from 'ui/views/widgets/memberInfoCommunicationSettingsEmail';
import { MemberInfoCommunicationSettingsEmailService } from 'services/memberInfoCommunicationSettingsEmail';
import * as confirmOverlay from 'utils/confirmOverlay';
import * as errorOverlay from 'utils/errorOverlay';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import { getLabel } from 'utils/getLabel';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION, SIZES, GLOBAL_LABELS } from 'constants/common';
import * as successOverlay from 'utils/successOverlay';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberInfoCommunicationSettingsEmail } from 'ui/containers/MemberInfoCommunicationSettingsEmail';
 *
 * // render
 * <MemberInfoCommunicationSettingsEmail propName='propValue'/>
 */
export class MemberInfoCommunicationSettingsEmail extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind functions
        this.addEditFormHandler = this.addEditFormHandler.bind( this );
        this.deleteActionHandler = this.deleteActionHandler.bind( this );
        this.handleSetCommunicationPreference = this.handleSetCommunicationPreference.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            showCustomErrorView: false,
            disableAddNewButton: true
        };

        // Member Info Email Settings Service
        this.memberInfoCommunicationSettingsEmailService = new MemberInfoCommunicationSettingsEmailService();

        // Member Info Email Settings data fetch
        this.fetchComponentData();
    }

    /**
     * @desc Handles fetching of component data
     */
    async fetchComponentData() {
        log.debug( 'MemberInfoCommunicationSettingsEmail.fetchComponentData()' );

        // cancel ongoing request
        this.memberInfoCommunicationSettingsEmailService.cancel();

        try {
            const { status, data }  = await this.memberInfoCommunicationSettingsEmailService.fetchData( {
                url: this.props.widget.url,
                data: this.props.requestPayload,
            } );

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
                if( ! isEmpty( get( data, 'description', {} ) ) ) {
                    this.setState( {
                        isFetching: false,
                        showCustomErrorView: true,
                        componentData: data
                    } );
                } else {
                    this.setState( {
                        isFetching: false,
                        networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                    } );
                }
            } else {

                // enable add new button if no email
                let disableAddNewButton = true;
                if ( isEmpty( data.contacts ) ) {
                    disableAddNewButton = false;
                }

                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    componentData: data,
                    disableAddNewButton
                } );
            }
        } catch( e ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
            } );
        }
    }

    /**
     * @desc Handles add/edit email form
     */
    async addEditFormHandler ( { id, contact } ) {
        log.debug( 'MemberInfoCommunicationSettingsEmail.addEditFormHandler()' );
        // get form add action from component data
        const formAction = get( this.state.componentData, 'addAction' );

        // cancel ongoing request
        this.memberInfoCommunicationSettingsEmailService.cancel();

        // prepare request payload
        const reqParams = cloneDeep( this.props.requestPayload );
        reqParams.context.operation = isNil( id ) ? CRUD_OPERATION.CREATE : CRUD_OPERATION.UPDATE;

        // show page level preloader
        pageLevelPreloader.show();
        
        // get status and service data
        const { _status, data } = await this.memberInfoCommunicationSettingsEmailService.addUpdateEmail( {
            url: formAction.config.submitUrl,
            data: merge(
                {
                    data: {
                        id,
                        type: 'email',
                        contact
                    }
                }, reqParams ),
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if ( SERVICE_RESPONSE_STATUS.SUCCESS === _status ) {
            const updateMemberPreferencesRequest = this.state.componentData.updateMemberPreferencesRequest;
            const emailObj = updateMemberPreferencesRequest.member.channelPreferences.communicationChannels.find( obj => 'EMAIL' == obj.channel.channelName );
            
            
                emailObj.channelAddress =  contact;

                // get status and service data
                const { status, data } = await this.memberInfoCommunicationSettingsEmailService.setCommunicationPreference( {
                    url: this.state.componentData.communicationPreferenceAction.url, //getting the url of action
                    data: merge(
                        {data: {memberChannelPreferences: updateMemberPreferencesRequest}},
                        this.props.requestPayload ),
                } );
            
            // fetch items from the backend again
            this.setState( {
                isFetching: true
            }, () => {
                this.fetchComponentData();
            } );
        } else {
            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /**
     * @desc Handles deletion of epost email
     */
     async deleteActionHandler( { item } ) {
        log.debug( 'MemberInfoCommunicationSettingsEmail.deleteActionHandler()' );
        
        // get delete action from component data
        const deleteAction = find( this.state.componentData.modifyActions, { name: 'delete' } );

        // ask user whether to proceed, via overlay
        const shouldProceed = await confirmOverlay.open( {
            message: getLabel( deleteAction.confirmation ),
            iconName: 'close'
        } );

        // if user wants to proceed
        if ( shouldProceed ) {

            // cancel ongoing request
            this.memberInfoCommunicationSettingsEmailService.cancel();

            // show page level preloader
            pageLevelPreloader.show();

            // prepare request payload
            const reqParams = cloneDeep( this.props.requestPayload );
            reqParams.context.operation = CRUD_OPERATION.DELETE;

            // get status and service data
            const { status, data } = await this.memberInfoCommunicationSettingsEmailService.deleteItem( {
                url: deleteAction.url,
                data: merge( {
                        data: {
                            id: item.id
                        }
                    }, reqParams ),
            } );

            // hide page level preloader
            pageLevelPreloader.hide();

            // check if response status was success
            if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
                this.setState( {
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    componentData: {
                        ...this.state.componentData,
                        contacts: filter(
                            this.state.componentData.contacts,
                            ( contact ) => contact.id !== item.id
                        ),
                    },
                    disableAddNewButton: false
                } );
            } else {
                
                // show error dialog
                if( ! isEmpty( get( data, 'description' ) ) ) {
                    errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
                } else {
                    errorOverlay.open();
                }
            }
        }
    
    }

     /*
     * @desc Handles set communication Preference action
     */
     
     async handleSetCommunicationPreference( payload ) {
        log.debug( 'MemberInfoCommunicationSettingsEmail.handleSetCommunicationPreference()' );
        const obj = payload;
        const {communicationPreferenceFlag} = payload;
        const emailObj = obj.updateMemberPreferencesRequest.member.channelPreferences.communicationChannels.find( obj => 'EMAIL' == obj.channel.channelName );
        emailObj.optedIn = payload.communicationPreferenceFlag;
        emailObj.channelAddress = this.state?.componentData?.contacts?.find( obj => obj)?.contact;

        // cancel ongoing request
        this.memberInfoCommunicationSettingsEmailService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberInfoCommunicationSettingsEmailService.setCommunicationPreference( {
            url: this.state.componentData.communicationPreferenceAction.url, //getting the url of action
            data: merge(
                {data: {memberChannelPreferences: obj.updateMemberPreferencesRequest}},
                  this.props.requestPayload ),
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: {
                    ...this.state.componentData,
                    ...communicationPreferenceFlag,
                },
            }, () => {
                successOverlay.open( {
                    message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                } );
            } );
        } else {

            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }

    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberInfoCommunicationSettingsEmailView
                isFetching= { this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                requestPayload={ this.props.requestPayload }
                { ...get( this.state, 'componentData', {} ) }
                showCustomErrorView={ this.state.showCustomErrorView }
                onAddEditClick = { this.addEditFormHandler }
                onDeleteClick = { this.deleteActionHandler }
                disableAddNewButton={ this.state.disableAddNewButton }
                onSetCommunicationPreferenceClick = { this.handleSetCommunicationPreference }
            />
        );
    }
}

// set display name
MemberInfoCommunicationSettingsEmail.displayName = 'MemberInfoCommunicationSettingsEmail';

// set default props
MemberInfoCommunicationSettingsEmail.defaultProps = {};

// prop types
MemberInfoCommunicationSettingsEmail.propTypes = {};
